import { useState, Fragment } from "react";
import { useStore } from "../contexts/StoreContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFlyers } from "../api/queries";
import { deleteFlyer } from "../api/mutations";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
  NewspaperIcon,
} from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import DeleteAlert from "../components/DeleteAlert";
import TrialEndedAlert from "../components/TrialEndedAlert";
import EmptyState from "../components/EmptyState";
import Header from "../components/Header";
import Button from "../components/Button";

export default function Flyers() {
  const { selectedStore, canManageStore } = useStore();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [deleteFlyerId, setDeleteFlyerId] = useState(null);

  const { data: flyers, isLoading } = useQuery({
    queryKey: ["flyers", selectedStore?.id],
    queryFn: () => fetchFlyers(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const deleteFlyerMutation = useMutation({
    mutationFn: deleteFlyer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flyers", selectedStore?.id],
      });
    },
  });

  const isInTrialPeriod = () => {
    if (!selectedStore?.trial_expires_at) return false;
    const trialEndDate = new Date(selectedStore.trial_expires_at);
    trialEndDate.setDate(trialEndDate.getDate());
    return new Date() < trialEndDate;
  };

  const handleOpenModal = (flyer) => {
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    } else {
      if (flyer) {
        navigate(`/flyers/${flyer.id}/edit`);
      } else {
        navigate(`/flyers/new`);
      }
    }
  };

  if (isLoading) {
    return <div>{t("common.loading")}</div>;
  }

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={NewspaperIcon}
          title={t("flyers.title")}
          description={t("flyers.description")}
          rightSection={
            canManageStore && (
              <Button onClick={() => handleOpenModal()} icon={PlusIcon}>
                {t("flyers.newButton")}
              </Button>
            )
          }
        />

        {flyers?.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:gap-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {flyers?.map((flyer) => (
              <div
                key={flyer.id}
                className="rounded-lg border border-gray-200 bg-gray-50 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                <div className="bg-gray-200 pl-4 p-2">
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-3">
                      {flyer.form.pass.name} - Flyer
                    </div>
                    {/* A member only gets the download, so without a PDF
                        there is nothing to put in the menu. invisible, not
                        hidden: the trigger sets the header's height. */}
                    <div
                      className={`flex space-x-3 ${canManageStore || flyer.pdf_url ? "" : "invisible"}`}
                    >
                      <Menu
                        as="div"
                        className="relative inline-block text-left"
                      >
                        <Menu.Button className="p-2 text-gray-500 hover:text-gray-700">
                          <EllipsisVerticalIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 z-50 mt-2 p-1 whitespace-nowrap origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                            {canManageStore && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleOpenModal(flyer)}
                                    className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-indigo-600 ${active ? "bg-gray-50" : ""}`}
                                  >
                                    <PencilIcon className="h-5 w-5 mr-2" />
                                    {t("flyers.actions.edit")}
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {flyer.pdf_url && (
                              <Menu.Item>
                                {({ active }) => (
                                  <a
                                    href={flyer.pdf_url}
                                    download={`flyer-${flyer.id}.pdf`}
                                    className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-gray-900 ${active ? "bg-gray-50" : ""}`}
                                  >
                                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                    {t("flyers.actions.download")}
                                  </a>
                                )}
                              </Menu.Item>
                            )}
                            {canManageStore && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => {
                                      setIsDeleteAlertOpen(true);
                                      setDeleteFlyerId(flyer.id);
                                    }}
                                    className={`flex w-full items-center px-3 py-1 text-sm leading-6 text-red-600 ${active ? "bg-gray-50" : ""}`}
                                  >
                                    <TrashIcon className="h-5 w-5 mr-2" />
                                    {t("flyers.actions.delete")}
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                </div>
                {/* Preview */}
                <div className="w-full">
                  {flyer.preview_url ? (
                    <img
                      src={flyer.preview_url}
                      alt={`Flyer ${flyer.id} preview`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full min-h-64 flex items-center justify-center text-gray-400">
                      {t("flyers.preview.noPreview")}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteFlyerMutation.mutate({
            storeId: selectedStore.id,
            flyerId: deleteFlyerId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
